// SPDX-License-Identifier: MIT
// Annotations panel tab content – renders inside Kepler's left side panel
// when the "Annotations" tab is active.
//
// Two-state UI:
//  State 1 (list): Shows annotation cards summarising each drawn feature.
//  State 2 (edit): Shows a staged-edit form for the selected feature with
//                  Save / Cancel / Delete buttons.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { selectEditorFeatures, selectEditorFeatureCount } from '../../../selectors/editorFeatures';
import { selectHasSecureDatasets } from '../../../selectors/exportControl';
import type { KeplerEditorFeature } from '../../../types/kepler-features';
import type { AppDispatch } from '../../../store';
import type { AnyAction } from 'redux';
import { wrapTo, fitBounds } from '@kepler.gl/actions';
import { setFeatures } from '@kepler.gl/actions';
import {
  selectSelectedFeatureId,
  selectAllAnnotations,
  selectAvailableTags,
  setSelectedFeature,
  addAvailableTag,
  deleteAllFeatureAnnotations,
  exportDrawnFeatures,
  loadAnnotationsFromGeoJSON,
  saveAnnotationDraft,
  type RootStateWithAnnotations,
  type FeatureAnnotation,
  createEmptyAnnotation,
} from '../feature-annotations-slice';
import {
  exportFeaturesAsGeoJSON,
  downloadGeoJSON,
  copyGeoJSONToClipboard,
  mergeFeaturesWithAnnotations,
  RESERVED_ANNOTATION_KEYS,
  parseAnnotationsFromGeoJSON,
} from '../../../utils/geojson-export';
import { addNotification } from '@kepler.gl/actions';

// Helper to cast actions for dispatch
const asAction = <T extends { type: string }>(action: T): AnyAction =>
  action as unknown as AnyAction;

type PanelView = 'list' | 'edit';

// ─── Styled Components ───────────────────────────────────────────────────────

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

// ── List view ─────────────────────────────────────────────────────────────────

const CardScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  color: ${props => props.theme.subtextColor};
  font-size: 12px;
  text-align: center;
  line-height: 1.6;
`;

const AnnotationCard = styled.button`
  width: calc(100% - 24px);
  display: block;
  text-align: left;
  margin: 0 12px 8px;
  padding: 12px;
  background: ${props => props.theme.panelBackground};
  border: 1px solid ${props => props.theme.borderColor}66;
  border-radius: 6px;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
  &:hover {
    border-color: ${props => props.theme.primaryColor}88;
    background: ${props => props.theme.panelBackgroundHover};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textColor};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subtextColor};
  margin-bottom: 6px;
`;

const CardTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
`;

const CardTagPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  background: ${props => props.theme.primaryColor}33;
  color: ${props => props.theme.primaryColor};
  border: 1px solid ${props => props.theme.primaryColor}55;
`;

const CardComment = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subtextColor};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardNoAnnotation = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subtextColor};
  opacity: 0.6;
  font-style: italic;
`;

// ── Edit view ─────────────────────────────────────────────────────────────────

const EditViewWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EditHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  flex-shrink: 0;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.primaryColor};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    opacity: 0.8;
  }
`;

const EditTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textColor};
`;

const EditFormContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 4px;
`;

const ActionButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px 4px;
  flex-shrink: 0;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.theme.primaryBtnBgd};
  color: ${props => props.theme.primaryBtnColor};
  &:hover {
    background: ${props => props.theme.primaryBtnBgdHover};
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 8px;
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: ${props => props.theme.textColor};
  &:hover {
    border-color: ${props => props.theme.primaryColor};
    color: ${props => props.theme.primaryColor};
  }
`;

const DeleteFeatureButton = styled.button`
  display: block;
  margin: 4px 16px 8px;
  padding: 8px;
  border: 1px solid ${props => props.theme.errorColor}88;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.theme.errorColor}1A;
  color: ${props => props.theme.errorColor};
  box-sizing: border-box;
  width: calc(100% - 32px);
  &:hover {
    background: ${props => props.theme.errorColor}33;
  }
`;

// ── Shared (form fields, tags, etc.) ──────────────────────────────────────────

const SectionLabel = styled.div`
  padding: 10px 16px 4px;
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const StyledInput = styled.input`
  width: 100%;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.inputBorderColor};
  border-radius: 3px;
  color: ${props => props.theme.inputColor};
  font-size: 12px;
  padding: 7px 10px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: ${props => props.theme.inputBorderActiveColor};
    background: ${props => props.theme.inputBgdActive};
  }
  &::placeholder {
    color: ${props => props.theme.subtextColor};
    opacity: 0.6;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.inputBorderColor};
  border-radius: 3px;
  color: ${props => props.theme.inputColor};
  font-size: 12px;
  padding: 7px 10px;
  outline: none;
  min-height: 72px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  &:focus {
    border-color: ${props => props.theme.inputBorderActiveColor};
    background: ${props => props.theme.inputBgdActive};
  }
  &::placeholder {
    color: ${props => props.theme.subtextColor};
    opacity: 0.6;
  }
`;

const FieldWrapper = styled.div`
  padding: 0 16px 8px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 16px 8px;
`;

const TagChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${props => (props.$active ? props.theme.primaryColor : props.theme.borderColor)};
  background: ${props => (props.$active ? props.theme.primaryColor + '33' : 'transparent')};
  color: ${props => (props.$active ? props.theme.primaryColor : props.theme.subtextColor)};
  transition: all 0.15s ease;
  &:hover {
    border-color: ${props => props.theme.primaryColor};
    color: ${props => props.theme.primaryColor};
  }
`;

const CustomTagInput = styled(StyledInput)``;

const CustomPropertiesTable = styled.div`
  margin: 0 16px 8px;
  border: 1px solid ${props => props.theme.borderColor}66;
  border-radius: 4px;
  overflow: hidden;
`;

const AnnotationRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid ${props => props.theme.borderColor}44;
  &:last-child {
    border-bottom: none;
  }
`;

const AnnotationKey = styled.div`
  flex: 0 0 40%;
  padding: 6px 10px;
  background: ${props => props.theme.panelBackground};
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
  font-weight: 600;
  word-break: break-all;
  border-right: 1px solid ${props => props.theme.borderColor}55;
  display: flex;
  align-items: center;
`;

const AnnotationValue = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: ${props => props.theme.textColor};
  font-size: 12px;
  padding: 6px 8px;
  outline: none;
  min-width: 0;
  &:focus {
    background: ${props => props.theme.inputBgdActive};
  }
`;

const DeleteAnnotationButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.subtextColor};
  cursor: pointer;
  padding: 0 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:hover {
    color: ${props => props.theme.errorColor};
  }
`;

const AddPropertyForm = styled.div`
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
`;

const AddPropertyRow = styled.div`
  display: flex;
  gap: 6px;
`;

const PropertyInput = styled.input`
  flex: 1;
  background: ${props => props.theme.inputBgd};
  border: 1px solid ${props => props.theme.inputBorderColor};
  border-radius: 3px;
  color: ${props => props.theme.inputColor};
  font-size: 12px;
  padding: 5px 8px;
  outline: none;
  min-width: 0;
  &:focus {
    border-color: ${props => props.theme.inputBorderActiveColor};
    background: ${props => props.theme.inputBgdActive};
  }
  &::placeholder {
    color: ${props => props.theme.subtextColor};
    opacity: 0.6;
  }
`;

const AddButton = styled.button`
  background: ${props => props.theme.primaryBtnBgd};
  color: ${props => props.theme.primaryBtnColor};
  border: none;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  padding: 7px 12px;
  cursor: pointer;
  width: 100%;
  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryBtnBgdHover};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Export / Import ───────────────────────────────────────────────────────────

const ExportSection = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${props => props.theme.borderColor};
  flex-shrink: 0;
`;

const ExportButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  background: ${props => props.theme.primaryBtnBgd};
  color: ${props => props.theme.primaryBtnColor};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryBtnBgdHover};
  }
`;

const SaveTip = styled.div`
  margin: 0 0 8px;
  padding: 8px 10px;
  background: ${props => props.theme.primaryColor}18;
  border: 1px solid ${props => props.theme.primaryColor}44;
  border-radius: 4px;
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
  line-height: 1.5;
`;

const ImportButton = styled.button`
  width: 100%;
  padding: 7px 16px;
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: ${props => props.theme.textColor};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.primaryColor};
    color: ${props => props.theme.primaryColor};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getFeatureLabel(
  feature: KeplerEditorFeature,
  index: number,
  annotationLabel?: string
): string {
  const type = feature.geometry?.type || 'Feature';
  const num = index + 1;
  if (annotationLabel) {
    return `${annotationLabel} (${type} #${num})`;
  }
  const idHint = feature.id ? ` (${String(feature.id).slice(0, 6)})` : '';
  return `${type} #${num}${idHint}`;
}

function formatCardMeta(feature: KeplerEditorFeature, annotation?: FeatureAnnotation): string {
  const type = feature.geometry?.type || 'Feature';
  const parts: string[] = [type];
  if (annotation?.author) parts.push(annotation.author);
  if (annotation?.updatedAt) {
    const d = new Date(annotation.updatedAt);
    if (!isNaN(d.getTime())) parts.push(d.toLocaleDateString());
  }
  return parts.join(' · ');
}

/**
 * Computes the bounding box [lngMin, latMin, lngMax, latMax] from a GeoJSON geometry.
 * Returns null if the geometry has no extractable coordinates.
 */
function getBoundsFromGeometry(
  geometry: KeplerEditorFeature['geometry']
): [number, number, number, number] | null {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;

  const processCoord = (coord: number[]) => {
    if (coord.length < 2) return;
    const [lng, lat] = coord;
    if (!isFinite(lng) || !isFinite(lat)) return;
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  };

  if (!geometry) return null;

  switch (geometry.type) {
    case 'Point':
      processCoord(geometry.coordinates as number[]);
      break;
    case 'MultiPoint':
    case 'LineString':
      for (const c of geometry.coordinates as number[][]) processCoord(c);
      break;
    case 'MultiLineString':
    case 'Polygon':
      for (const ring of geometry.coordinates as number[][][])
        for (const c of ring) processCoord(c);
      break;
    case 'MultiPolygon':
      for (const poly of geometry.coordinates as number[][][][])
        for (const ring of poly) for (const c of ring) processCoord(c);
      break;
    default:
      return null;
  }

  if (!isFinite(minLng) || !isFinite(minLat) || !isFinite(maxLng) || !isFinite(maxLat)) return null;
  return [minLng, minLat, maxLng, maxLat];
}

// Export icon SVG
const ExportIcon = () => (
  <svg viewBox='0 0 24 24' height='16px' fill='currentColor'>
    <path d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z' />
  </svg>
);

// Import icon SVG
const ImportIcon = () => (
  <svg viewBox='0 0 24 24' height='16px' fill='currentColor'>
    <path d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,11L16,15H13.5V18H10.5V15H8L12,11Z' />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────

export const AnnotationsPanelTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // ── Redux state ─────────────────────────────────────────────────────────
  const reduxSelectedId = useSelector((state: RootStateWithAnnotations) =>
    selectSelectedFeatureId(state)
  );
  const allAnnotations = useSelector((state: RootStateWithAnnotations) =>
    selectAllAnnotations(state)
  );
  const availableTags = useSelector((state: RootStateWithAnnotations) =>
    selectAvailableTags(state)
  );
  const features = useSelector(selectEditorFeatures, shallowEqual);
  const featureCount = useSelector(selectEditorFeatureCount);
  const hasSecure = useSelector(selectHasSecureDatasets);

  // ── Two-state view ──────────────────────────────────────────────────────
  const [view, setView] = useState<PanelView>('list');
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Omit<FeatureAnnotation, 'updatedAt'> | null>(null);

  // ── Form inputs ─────────────────────────────────────────────────────────
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep a ref to avoid stale closure in navigation effect
  const allAnnotationsRef = useRef(allAnnotations);
  allAnnotationsRef.current = allAnnotations;

  // Guard ref: prevents Effect 2 from re-entering when a Redux dispatch inside
  // closeEditView triggers a synchronous react-redux re-render before the local
  // state updates (setEditingFeatureId/setView) have been committed.
  const isAutoClosingRef = useRef(false);

  // ── Navigation helpers ──────────────────────────────────────────────────

  const openEditView = useCallback((featureId: string) => {
    const annotation = allAnnotationsRef.current[featureId] || createEmptyAnnotation();
    setEditingFeatureId(featureId);
    setDraft({
      label: annotation.label,
      tags: [...annotation.tags],
      comment: annotation.comment,
      author: annotation.author,
      customProperties: { ...annotation.customProperties },
    });
    setView('edit');
    setNewKey('');
    setNewValue('');
    setCustomTag('');
  }, []);

  const closeEditView = useCallback(() => {
    dispatch(asAction(setSelectedFeature(null)));
    setView('list');
    setEditingFeatureId(null);
    setDraft(null);
    setNewKey('');
    setNewValue('');
    setCustomTag('');
  }, [dispatch]);

  // ── Card click ──────────────────────────────────────────────────────────

  const handleCardClick = useCallback(
    (featureId: string) => {
      dispatch(asAction(setSelectedFeature(featureId)));
      openEditView(featureId);
      // Zoom the map to the clicked feature's bounds with padding so the
      // polygon occupies at most 50% of the viewport in each dimension.
      const feature = features.find(f => String(f.id) === featureId);
      if (feature?.geometry) {
        const bounds = getBoundsFromGeometry(feature.geometry);
        if (bounds) {
          const [minLng, minLat, maxLng, maxLat] = bounds;
          const lngPad = Math.max((maxLng - minLng) * 0.5, 0.001);
          const latPad = Math.max((maxLat - minLat) * 0.5, 0.001);
          const paddedBounds: [number, number, number, number] = [
            minLng - lngPad,
            minLat - latPad,
            maxLng + lngPad,
            maxLat + latPad,
          ];
          dispatch(wrapTo('map', fitBounds(paddedBounds) as unknown as AnyAction));
        }
      }
    },
    [dispatch, openEditView, features]
  );

  // ── Right-click from map: navigate to edit view ─────────────────────────
  // When FeatureHoverTooltip dispatches setSelectedFeature, auto-open edit.
  useEffect(() => {
    if (reduxSelectedId != null && reduxSelectedId !== editingFeatureId) {
      openEditView(reduxSelectedId);
    }
  }, [reduxSelectedId, editingFeatureId, openEditView]);

  // ── Handle deletion of the feature being edited ─────────────────────────
  const annotableFeatures = React.useMemo(() => features.filter(f => f.id != null), [features]);

  useEffect(() => {
    // Reset the guard whenever the edit view is not active so subsequent
    // openings are handled correctly.
    if (!editingFeatureId || view !== 'edit') {
      isAutoClosingRef.current = false;
      return;
    }

    // Prevent re-entrant calls: react-redux's useSyncExternalStore may trigger
    // a synchronous re-render immediately after the dispatch inside closeEditView,
    // before the local useState updates (setEditingFeatureId / setView) commit.
    // Without this guard that intermediate render — which still sees the stale
    // editingFeatureId value — would call closeEditView again, causing an
    // infinite "Maximum update depth exceeded" crash (React error #185, see PR #151).
    if (isAutoClosingRef.current) return;

    const stillExists = annotableFeatures.some(f => String(f.id) === editingFeatureId);
    if (!stillExists) {
      isAutoClosingRef.current = true;
      closeEditView();
    }
  }, [annotableFeatures, editingFeatureId, view, closeEditView]);

  // ── Draft change handlers ───────────────────────────────────────────────

  const handleDraftLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(d => (d ? { ...d, label: e.target.value } : d));
  }, []);

  const handleDraftCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(d => (d ? { ...d, comment: e.target.value } : d));
  }, []);

  const handleDraftAuthorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(d => (d ? { ...d, author: e.target.value } : d));
  }, []);

  const handleDraftToggleTag = useCallback((tag: string) => {
    setDraft(d => {
      if (!d) return d;
      const hasTag = d.tags.includes(tag);
      return { ...d, tags: hasTag ? d.tags.filter(t => t !== tag) : [...d.tags, tag] };
    });
  }, []);

  const handleDraftAddCustomTag = useCallback(() => {
    const trimmed = customTag.trim();
    if (!trimmed) return;
    // Immediately add to the global tag pool so it shows up in the chip list
    dispatch(asAction(addAvailableTag(trimmed)));
    // Add to draft's selected tags
    setDraft(d => {
      if (!d) return d;
      if (d.tags.includes(trimmed)) return d;
      return { ...d, tags: [...d.tags, trimmed] };
    });
    setCustomTag('');
  }, [dispatch, customTag]);

  const handleCustomTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleDraftAddCustomTag();
      }
    },
    [handleDraftAddCustomTag]
  );

  const handleDraftValueChange = useCallback((key: string, value: string) => {
    setDraft(d => (d ? { ...d, customProperties: { ...d.customProperties, [key]: value } } : d));
  }, []);

  const handleDraftDeleteProperty = useCallback((key: string) => {
    setDraft(d => {
      if (!d) return d;
      const updated = { ...d.customProperties };
      delete updated[key];
      return { ...d, customProperties: updated };
    });
  }, []);

  const handleDraftAddProperty = useCallback(() => {
    if (!newKey.trim()) return;
    const trimmedKey = newKey.trim();
    if (RESERVED_ANNOTATION_KEYS.has(trimmedKey)) {
      dispatch(
        addNotification({
          id: 'annotation-reserved-key',
          type: 'error',
          message: `"${trimmedKey}" is a reserved annotation field. Use a different property name.`,
        }) as any
      );
      return;
    }
    setDraft(d =>
      d ? { ...d, customProperties: { ...d.customProperties, [trimmedKey]: newValue } } : d
    );
    setNewKey('');
    setNewValue('');
  }, [dispatch, newKey, newValue]);

  const handleKeyKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      valueInputRef.current?.focus();
    }
  }, []);

  const handleValueKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleDraftAddProperty();
      }
    },
    [handleDraftAddProperty]
  );

  // ── Save / Cancel / Delete ──────────────────────────────────────────────

  const handleSave = useCallback(() => {
    if (!editingFeatureId || !draft) return;
    dispatch(asAction(saveAnnotationDraft(editingFeatureId, draft)));
    closeEditView();
  }, [dispatch, editingFeatureId, draft, closeEditView]);

  const handleCancel = useCallback(() => {
    closeEditView();
  }, [closeEditView]);

  const handleDeleteFeature = useCallback(() => {
    if (!editingFeatureId) return;
    // Remove feature from Kepler.gl editor
    const updatedFeatures = features.filter(f => String(f.id) !== editingFeatureId);
    dispatch(wrapTo('map', setFeatures(updatedFeatures as any[]) as unknown as AnyAction));
    // Remove annotations
    dispatch(asAction(deleteAllFeatureAnnotations(editingFeatureId)));
    closeEditView();
  }, [dispatch, editingFeatureId, features, closeEditView]);

  // ── Export ──────────────────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    dispatch(asAction(exportDrawnFeatures()));
    if (hasSecure) return;
    if (featureCount === 0) {
      dispatch(
        addNotification({
          id: 'export-drawn-empty',
          type: 'info',
          message: 'No drawn features to export. Draw features on the map first.',
        }) as any
      );
      return;
    }
    try {
      setIsExporting(true);
      const withAnnotations = mergeFeaturesWithAnnotations(features, allAnnotations);
      if (withAnnotations.length === 0) {
        dispatch(
          addNotification({
            id: 'export-drawn-no-features',
            type: 'info',
            message: 'No features found to export.',
          }) as any
        );
        return;
      }
      const geoJson = exportFeaturesAsGeoJSON(withAnnotations);
      try {
        await copyGeoJSONToClipboard(geoJson);
        dispatch(
          addNotification({
            id: 'export-drawn-success',
            type: 'success',
            message: `Copied ${withAnnotations.length} feature(s) to clipboard as GeoJSON`,
          }) as any
        );
      } catch {
        downloadGeoJSON(geoJson, 'drawn-features.geojson');
        dispatch(
          addNotification({
            id: 'export-drawn-download',
            type: 'success',
            message: `Downloaded ${withAnnotations.length} feature(s) as GeoJSON file`,
          }) as any
        );
      }
    } catch (error) {
      console.error('Export drawn features error:', error);
      dispatch(
        addNotification({
          id: 'export-drawn-error',
          type: 'error',
          message: 'Failed to export drawn features. Please try again.',
        }) as any
      );
    } finally {
      setIsExporting(false);
    }
  }, [hasSecure, featureCount, features, allAnnotations, dispatch]);

  // ── Import ──────────────────────────────────────────────────────────────

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      setIsImporting(true);
      try {
        const text = await file.text();
        const json = JSON.parse(text) as unknown;
        const {
          annotations: imported,
          availableTags: importedTags,
          features: importedFeatures,
        } = parseAnnotationsFromGeoJSON(json);

        dispatch(wrapTo('map', setFeatures(importedFeatures as any[]) as unknown as AnyAction));
        dispatch(asAction(loadAnnotationsFromGeoJSON(imported, importedTags)));

        const fCount = importedFeatures.length;
        const annotCount = Object.keys(imported).length;
        dispatch(
          addNotification({
            id: 'import-annotations-success',
            type: 'success',
            message: `Imported ${fCount} feature(s)${annotCount > 0 ? ` with annotations for ${annotCount}` : ''} from GeoJSON`,
          }) as any
        );
      } catch (err) {
        dispatch(
          addNotification({
            id: 'import-annotations-error',
            type: 'error',
            message: `Failed to import GeoJSON: ${err instanceof Error ? err.message : 'Unknown error'}`,
          }) as any
        );
      } finally {
        setIsImporting(false);
      }
    },
    [dispatch]
  );

  // ── Render helpers ──────────────────────────────────────────────────────

  const draftCustomEntries = draft ? Object.entries(draft.customProperties) : [];

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <PanelContent data-testid='annotations-panel-tab'>
      {view === 'list' ? (
        /* ─── State 1: Card list ─── */
        <CardScrollArea>
          {features.length === 0 ? (
            <EmptyState>
              No drawn features yet.
              <br />
              Use the draw tools on the map to create polygons, points, or lines.
            </EmptyState>
          ) : (
            features.map((feature, index) => {
              const featureId = feature.id != null ? String(feature.id) : null;
              const annotation = featureId ? allAnnotations[featureId] : undefined;
              const hasContent =
                annotation &&
                (annotation.label ||
                  annotation.tags.length > 0 ||
                  annotation.comment ||
                  annotation.author ||
                  Object.keys(annotation.customProperties).length > 0);

              return (
                <AnnotationCard
                  key={featureId ?? `no-id-${index}`}
                  data-testid={
                    featureId ? `annotation-card-${featureId}` : `annotation-card-no-id-${index}`
                  }
                  type='button'
                  disabled={!featureId}
                  onClick={() => featureId && handleCardClick(featureId)}
                  title={
                    featureId ? undefined : 'This feature has no stable ID and cannot be annotated'
                  }
                >
                  <CardTitle>{getFeatureLabel(feature, index, annotation?.label)}</CardTitle>
                  <CardMeta>{formatCardMeta(feature, annotation)}</CardMeta>
                  {annotation && annotation.tags.length > 0 && (
                    <CardTagsRow>
                      {annotation.tags.map(tag => (
                        <CardTagPill key={tag}>{tag}</CardTagPill>
                      ))}
                    </CardTagsRow>
                  )}
                  {annotation?.comment && <CardComment>{annotation.comment}</CardComment>}
                  {!hasContent && (
                    <CardNoAnnotation>No annotations yet. Click to add.</CardNoAnnotation>
                  )}
                </AnnotationCard>
              );
            })
          )}
        </CardScrollArea>
      ) : (
        /* ─── State 2: Edit form ─── */
        <EditViewWrapper>
          <EditHeader>
            <BackButton onClick={handleCancel} data-testid='edit-back-button'>
              ← Back
            </BackButton>
            <EditTitle>Edit Annotation</EditTitle>
          </EditHeader>

          <EditFormContent>
            {/* Label */}
            <SectionLabel>Label</SectionLabel>
            <FieldWrapper>
              <StyledInput
                value={draft?.label ?? ''}
                onChange={handleDraftLabelChange}
                placeholder='Feature label'
                aria-label='Feature label'
                data-testid='annotation-label-input'
              />
            </FieldWrapper>

            {/* Tags */}
            <SectionLabel>Tags</SectionLabel>
            <TagsContainer data-testid='tags-container'>
              {availableTags.map(tag => (
                <TagChip
                  key={tag}
                  $active={draft?.tags.includes(tag) ?? false}
                  onClick={() => handleDraftToggleTag(tag)}
                  data-testid={`tag-chip-${tag}`}
                >
                  {tag}
                </TagChip>
              ))}
            </TagsContainer>

            <SectionLabel>Custom Tag (optional)</SectionLabel>
            <FieldWrapper>
              <CustomTagInput
                value={customTag}
                onChange={e => setCustomTag(e.target.value)}
                onKeyDown={handleCustomTagKeyDown}
                placeholder='Press Enter to add'
                aria-label='Custom tag'
                data-testid='custom-tag-input'
              />
            </FieldWrapper>

            {/* Comment */}
            <SectionLabel>Comment</SectionLabel>
            <FieldWrapper>
              <StyledTextarea
                value={draft?.comment ?? ''}
                onChange={handleDraftCommentChange}
                placeholder='Add a comment…'
                aria-label='Comment'
                data-testid='annotation-comment-input'
              />
            </FieldWrapper>

            {/* Author */}
            <SectionLabel>Author</SectionLabel>
            <FieldWrapper>
              <StyledInput
                value={draft?.author ?? ''}
                onChange={handleDraftAuthorChange}
                placeholder='Your name'
                aria-label='Author'
                data-testid='annotation-author-input'
              />
            </FieldWrapper>

            {/* Custom Properties */}
            <SectionLabel>Custom Properties</SectionLabel>
            {draftCustomEntries.length > 0 && (
              <CustomPropertiesTable>
                {draftCustomEntries.map(([key, value]) => (
                  <AnnotationRow key={key} data-testid={`annotation-row-${key}`}>
                    <AnnotationKey title={key}>{key}</AnnotationKey>
                    <AnnotationValue
                      value={value}
                      onChange={e => handleDraftValueChange(key, e.target.value)}
                      aria-label={`Value for ${key}`}
                      data-testid={`annotation-value-${key}`}
                    />
                    <DeleteAnnotationButton
                      onClick={() => handleDraftDeleteProperty(key)}
                      aria-label={`Delete property ${key}`}
                      data-testid={`delete-annotation-${key}`}
                      title='Delete property'
                    >
                      ×
                    </DeleteAnnotationButton>
                  </AnnotationRow>
                ))}
              </CustomPropertiesTable>
            )}

            <AddPropertyForm>
              <AddPropertyRow>
                <PropertyInput
                  placeholder='Key'
                  value={newKey}
                  onChange={e => setNewKey(e.target.value)}
                  onKeyDown={handleKeyKeyDown}
                  aria-label='New property key'
                  data-testid='new-key-input'
                />
                <PropertyInput
                  ref={valueInputRef}
                  placeholder='Value'
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  onKeyDown={handleValueKeyDown}
                  aria-label='New property value'
                  data-testid='new-value-input'
                />
              </AddPropertyRow>
              <AddButton
                onClick={handleDraftAddProperty}
                disabled={!newKey.trim()}
                data-testid='add-annotation-button'
              >
                Add Property
              </AddButton>
            </AddPropertyForm>

            {/* Save / Cancel */}
            <ActionButtonsRow>
              <SaveButton onClick={handleSave} data-testid='edit-save-button'>
                Save Feature
              </SaveButton>
              <CancelButton onClick={handleCancel} data-testid='edit-cancel-button'>
                Cancel
              </CancelButton>
            </ActionButtonsRow>

            {/* Delete */}
            <DeleteFeatureButton onClick={handleDeleteFeature} data-testid='edit-delete-button'>
              Delete This Annotation
            </DeleteFeatureButton>
          </EditFormContent>
        </EditViewWrapper>
      )}

      {/* Export / Import – always visible at bottom */}
      <ExportSection>
        <SaveTip>
          💾 <strong>Tip:</strong> Annotations are not saved between sessions. Export your work as
          GeoJSON and re-import to continue where you left off.
        </SaveTip>
        <ExportButton
          onClick={handleExport}
          disabled={hasSecure || isExporting || featureCount === 0}
          $disabled={hasSecure || isExporting || featureCount === 0}
          data-testid='panel-export-geojson'
        >
          <ExportIcon />
          {hasSecure
            ? 'Export Restricted'
            : featureCount === 0
              ? 'No Features to Export'
              : `Export ${featureCount} Feature(s) as GeoJSON`}
        </ExportButton>
        <ImportButton
          onClick={handleImportClick}
          disabled={isImporting}
          data-testid='panel-import-geojson'
          title='Import a previously exported annotated GeoJSON to restore features and annotations'
        >
          <ImportIcon />
          {isImporting ? 'Importing…' : 'Import Annotated GeoJSON'}
        </ImportButton>
        {/* Hidden file input – triggered by ImportButton */}
        <input
          ref={fileInputRef}
          type='file'
          accept='.geojson,.json,application/json'
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid='import-file-input'
        />
      </ExportSection>
    </PanelContent>
  );
};

AnnotationsPanelTab.displayName = 'AnnotationsPanelTab';

export default AnnotationsPanelTab;
